import { useState, useEffect } from "react";
import "../../../assets/css/loan.css";
import TableControls from "../../../components/TableControls";
import BaseModal from "../../../components/BaseModal";
import { useAuth } from "../../../context/AuthContext";

const FETCH_API =
  process.env.REACT_APP_API_BASE + "/loans/loans.php?status=approved,active";

const UPDATE_API =
  process.env.REACT_APP_API_BASE + "/loans/loans.php";

  const FILE_BASE = process.env.REACT_APP_API_BASE;

  const documents = [
  { key: "passport", label: "Passport Photograph", icon: "📷" },
  { key: "id_card", label: "ID Card", icon: "🪪" },
  { key: "payslip", label: "Payslip", icon: "📄" },
  { key: "bank_statement", label: "Bank Statement", icon: "🏦" },
  { key: "asset_photo", label: "Asset Photo", icon: "🚗" },
  { key: "signature", label: "Signature", icon: "✍️" },
];



/* ================= TOAST ================= */
function Toast({ toast, onClose }) {
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [toast, onClose]);

  if (!toast) return null;

  return (
    <div className={`app-toast ${toast.type}`}>
      <div className="toast-icon">
        {toast.type === "success" ? "✓" : "✕"}
      </div>
      <div className="toast-content">
        <strong>{toast.title}</strong>
        <p>{toast.message}</p>
      </div>
      <button className="toast-close" onClick={onClose}>×</button>
    </div>
  );
}

export default function ApprovedLoans() {
  
  const { user } = useAuth();

  const [loanList, setLoanList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [approvedAmount, setApprovedAmount] = useState("");
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
const [selectedMonth, setSelectedMonth] = useState("");
const [selectedRM, setSelectedRM] = useState("");

const [isReportModalOpen, setIsReportModalOpen] = useState(false);
const [reportStartDate, setReportStartDate] = useState("");
const [reportEndDate, setReportEndDate] = useState("");
const [reportType, setReportType] = useState("summary_rm");

  const [previewDoc, setPreviewDoc] = useState(null);
const [confirmAction, setConfirmAction] = useState(null);
// values: "update" | "close" | null

  const startIndex = (currentPage - 1) * rowsPerPage;

  const showToast = (type, title, message) => {
    setToast({ type, title, message });
  };

const handleDownloadReport = async () => {
  try {
    const params = new URLSearchParams({
      report: "1",
      report_type: reportType,
      start_date: reportStartDate,
      end_date: reportEndDate,
    });

    const url = `${process.env.REACT_APP_API_BASE}/loans/loans.php?status=approved,active&${params.toString()}`;
    const res = await fetch(url, {
      credentials: "include",
    });

    if (!res.ok) {
      throw new Error("Failed to download report");
    }

    const blob = await res.blob();
    const downloadUrl = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download =
      reportType === "summary_rm"
        ? "approved_loans_summary_per_rm.csv"
        : "approved_loans_detailed.csv";

    document.body.appendChild(link);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(downloadUrl);
    setIsReportModalOpen(false);
  } catch (error) {
    showToast("error", "Download Error", error.message);
  }
};

  /* ================= FETCH APPROVED LOANS ================= */
  const fetchLoans = async () => {
    try {
      setLoading(true);

      const res = await fetch(FETCH_API, {
        credentials: "include",
      });

      if (!res.ok) throw new Error("Failed to fetch loans");

      const response = await res.json();

      const safeData = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
        ? response.data
        : [];

      setLoanList(safeData);
      setCurrentPage(1);
    } catch (error) {
      showToast("error", "Error", error.message);
      setLoanList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLoans();
  }, []);

  useEffect(() => {
  setCurrentPage(1);
}, [selectedMonth, selectedRM]);
  
/* ================= OPEN MODAL ================= */
  const openModal = (loan) => {
    setSelectedLoan(loan);
   setApprovedAmount(
  loan.approved_amount ?? loan.loan_amount ?? ""
);
  };

  /* ================= UPDATE APPROVED AMOUNT ================= */
  const handleUpdate = async () => {
    if (!selectedLoan) return;

    if (!approvedAmount || Number(approvedAmount) <= 0) {
      showToast("error", "Invalid Amount", "Enter valid approved amount");
      return;
    }

    if (Number(approvedAmount) > Number(selectedLoan.loan_amount)) {
      showToast(
        "error",
        "Invalid Amount",
        "Approved amount cannot exceed requested amount"
      );
      return;
    }

    try {
      setSaving(true);

      const res = await fetch(UPDATE_API, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: selectedLoan.id,
          status: "approved",
          approved_amount: approvedAmount,
        }),
      });

      const result = await res.json();

      if (!res.ok) {
        showToast("error", "Error", result.message || "Update failed");
        return;
      }

      showToast("success", "Success", "Loan updated successfully");

      setSelectedLoan(null);
      setApprovedAmount("");
      fetchLoans();
    } catch (error) {
      showToast("error", "Server Error", "Something went wrong");
    } finally {
      setSaving(false);
    }
  };


const handleCloseLoan = async () => {
  if (!selectedLoan) return;

  try {
    setSaving(true);

    const res = await fetch(UPDATE_API, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id: selectedLoan.id,
        status: "closed",
      }),
    });

    const result = await res.json();

    if (!res.ok) {
      showToast("error", "Error", result.message || "Close failed");
      return;
    }

    showToast("success", "Success", "Loan closed successfully");

    setSelectedLoan(null);
    fetchLoans();

  } catch (error) {
    showToast("error", "Server Error", "Something went wrong");
  } finally {
    setSaving(false);
  }
};


const rmOptions = [
  ...new Set(
    loanList
      .map((loan) => loan.rm_name)
      .filter((rm) => rm && rm.trim() !== "")
  ),
];

const filteredLoans = loanList.filter((loan) => {
  const loanDate = loan.reviewed_at || loan.updated_at || loan.created_at || "";
const parsedDate = loanDate ? new Date(loanDate) : null;
const loanMonth =
  parsedDate && !isNaN(parsedDate.getTime())
    ? parsedDate.toISOString().slice(0, 7)
    : "";

  const matchesMonth = selectedMonth ? loanMonth === selectedMonth : true;
  const matchesRM = selectedRM ? loan.rm_name === selectedRM : true;

  return matchesMonth && matchesRM;
});

const totalApprovedLoans = filteredLoans.length;

const totalRequestedAmount = filteredLoans.reduce(
  (sum, loan) => sum + Number(loan.loan_amount || 0),
  0
);

const totalApprovedAmount = filteredLoans.reduce(
  (sum, loan) => sum + Number(loan.approved_amount ?? loan.loan_amount ?? 0),
  0
);

const paginatedLoans = filteredLoans.slice(
  startIndex,
  startIndex + rowsPerPage
);

  return (
    <div className="staff-page">
      <Toast toast={toast} onClose={() => setToast(null)} />

      <div className="page-header">
  <div className="page-header-left">
    <h2 className="page-title">Approved Loans</h2>
    <p className="page-subtitle">
      Loans approved and ready for disbursement
    </p>
  </div>

  <div className="page-header-actions">
    <button
      className="btn-primary"
      onClick={() => setIsReportModalOpen(true)}
    >
      Download Report
    </button>
  </div>
</div>

<div className="loan-summary-grid">
  <div className="summary-card">
    <h4>Total Approved Loans</h4>
    <p>{totalApprovedLoans}</p>
  </div>

  <div className="summary-card">
    <h4>Total Requested</h4>
    <p>₦{totalRequestedAmount.toLocaleString()}</p>
  </div>

  <div className="summary-card">
    <h4>Total Approved</h4>
    <p>₦{totalApprovedAmount.toLocaleString()}</p>
  </div>
</div>

<div className="card" style={{ marginBottom: "16px" }}>
  <div className="table-filters">
    <div className="filter-group">
      <label>Filter by Month</label>
      <input
        type="month"
        value={selectedMonth}
        onChange={(e) => setSelectedMonth(e.target.value)}
        className="form-input"
      />
    </div>

    <div className="filter-group">
      <label>Filter by RM</label>
      <select
        value={selectedRM}
        onChange={(e) => setSelectedRM(e.target.value)}
        className="form-input"
      >
        <option value="">All Relationship Managers</option>
        {rmOptions.map((rm) => (
          <option key={rm} value={rm}>
            {rm}
          </option>
        ))}
      </select>
    </div>

    <div className="filter-group">
      <label>&nbsp;</label>
      <button
        className="btn-outline"
        onClick={() => {
          setSelectedMonth("");
          setSelectedRM("");
        }}
      >
        Reset Filters
      </button>
    </div>
  </div>
</div>

<div className="card">
        <TableControls
          totalItems={filteredLoans.length}
          currentPage={currentPage}
          rowsPerPage={rowsPerPage}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={setRowsPerPage}
        />

       {loading ? (
 <div className="approved-loans-loader">
  <div className="approved-loans-spinner"></div>
  <span>Loading loans...</span>
</div>
) : (
          <div className="table-wrapper">
            <table>
              <thead>
             <tr>
  <th>S/N</th>
  <th>Applicant</th>
  <th>Product</th>
  <th>Requested</th>
  <th>Approved</th>
  <th>Tenor</th>
  <th>RM</th>
  <th>Status</th>
  <th>Action</th>
</tr>
              </thead>

              <tbody>
                {paginatedLoans.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="no-data-cell">
                      No approved loans found
                    </td>
                  </tr>
                ) : (
                 paginatedLoans.map((loan, index) => (
                    <tr key={loan.id}>
  <td>{startIndex + index + 1}</td>
  <td>{loan.customer_name}</td>
  <td>{loan.loan_product || "-"}</td>
  <td>
    ₦{Number(loan.loan_amount || 0).toLocaleString()}
  </td>
  <td>
    ₦{Number(loan.approved_amount ?? loan.loan_amount ?? 0).toLocaleString()}
  </td>
  <td>
    {loan.loan_tenor ? `${loan.loan_tenor} months` : "-"}
  </td>
  <td>{loan.rm_name || "-"}</td>
  <td>
    <span className="badge badge-success">
      {loan.status}
    </span>
  </td>
  <td>
    <button
      className="action-btn"
      onClick={() => openModal(loan)}
    >
      View
    </button>
  </td>
</tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ================= DETAILS MODAL ================= */}
      <BaseModal
     isOpen={!!selectedLoan}
     title="Loan Application Details"
     onClose={() => setSelectedLoan(null)}
     size="lg"
   >
     {selectedLoan && (
       <div className="loan-details-grid">
   
         {/* ================= APPLICANT ================= */}
        <div className="loan-section">
     <h4>Applicant Information</h4>
   
     <div className="loan-info-grid">
   
       {/* ✅ NEW CUSTOMER NAME */}
       <div className="loan-info-item full-width">
         <span className="loan-info-label">Customer Name</span>
         <span className="loan-info-value">
           {selectedLoan.customer_name ||
             `${selectedLoan.first_name} ${selectedLoan.last_name}`}
         </span>
       </div>
   
       <div className="loan-info-item">
         <span className="loan-info-label">Product</span>
         <span className="loan-info-value">
           {selectedLoan.loan_product || "-"}
         </span>
       </div>
   
       <div className="loan-info-item">
         <span className="loan-info-label">Amount</span>
         <span className="loan-info-value">
           ₦{Number(selectedLoan.loan_amount || 0).toLocaleString()}
         </span>
       </div>
   
       <div className="loan-info-item">
         <span className="loan-info-label">Tenor</span>
         <span className="loan-info-value">
           {selectedLoan.loan_tenor
             ? `${selectedLoan.loan_tenor} months`
             : "-"}
         </span>
       </div>
   
       <div className="loan-info-item">
         <span className="loan-info-label">Status</span>
         <span className="loan-info-value">
           {selectedLoan.status}
         </span>
       </div>
   
     </div>
   </div>
   
   
   
   
         {/* ================= EMPLOYMENT ================= */}
         <div className="loan-section">
           <h4>Employment Details</h4>
   
           <div className="loan-info-grid">
             <div className="loan-info-item">
               <span className="loan-info-label">Employer</span>
               <span className="loan-info-value">{selectedLoan.employer}</span>
             </div>
   
             <div className="loan-info-item">
               <span className="loan-info-label">Grade Level</span>
               <span className="loan-info-value">{selectedLoan.grade_level}</span>
             </div>
   
             <div className="loan-info-item">
               <span className="loan-info-label">Date of Employment</span>
               <span className="loan-info-value">
                 {selectedLoan.date_of_employment}
               </span>
             </div>
           </div>
         </div>
   
         {/* ================= RELATIONSHIP MANAGER ================= */}
   {(selectedLoan.rm_name || selectedLoan.rm_email) && (
     <div className="loan-section">
       <h4>Relationship Manager</h4>
   
       <div className="loan-info-grid">
         <div className="loan-info-item">
           <span className="loan-info-label">RM Name</span>
           <span className="loan-info-value">
             {selectedLoan.rm_name || "-"}
           </span>
         </div>
   
         <div className="loan-info-item">
           <span className="loan-info-label">RM Email</span>
           <span className="loan-info-value">
             {selectedLoan.rm_email || "-"}
           </span>
         </div>
   
         <div className="loan-info-item">
           <span className="loan-info-label">RM Code</span>
           <span className="loan-info-value">
             {selectedLoan.rm_code || "-"}
           </span>
         </div>
       </div>
     </div>
   )}
   
         {/* ================= DOCUMENTS ================= */}
   {/* ================= DOCUMENTS ================= */}
   <div className="loan-section">
     <h4>Supporting Documents</h4>
   
     <div className="doc-grid">
       {documents.map((doc) =>
         selectedLoan?.[doc.key] ? (
           <div
             key={doc.key}
             className="doc-card"
             onClick={() =>
               setPreviewDoc(`${FILE_BASE}/${selectedLoan[doc.key]}`)
             }
             style={{ cursor: "pointer" }}
           >
             <div className="doc-icon">{doc.icon}</div>
   
             <div className="doc-info">
               <span className="doc-title">{doc.label}</span>
               <span className="doc-sub">
                 Click to view document
               </span>
             </div>
           </div>
         ) : null
       )}
     </div>
   </div>
   
   {previewDoc && (
     <div className="doc-preview-overlay">
       <div className="doc-preview-header">
         <h4>Document Preview</h4>
   
         <button
           className="doc-preview-close"
           onClick={() => setPreviewDoc(null)}
         >
           ✕
         </button>
       </div>
   
       <div className="doc-preview-body">
         <iframe
           src={previewDoc}
           title="Document Preview"
           frameBorder="0"
         />
       </div>
     </div>
   )}
   
   {/* ================= FULL APPLICATION HISTORY ================= */}
   {selectedLoan.history && selectedLoan.history.length > 0 && (
     <div className="loan-section">
       <h4>Application History</h4>
   
       <div className="history-timeline">
         {selectedLoan.history.map((item, index) => {
           const statusLabel = item.action
             ?.replaceAll("_", " ")
             .toUpperCase();
   
           const badgeClass =
             item.action === "approved"
               ? "badge-success"
               : item.action === "rejected"
               ? "badge-danger"
               : item.action === "returned"
               ? "badge-secondary"
               : item.action === "level_one_approved"
               ? "badge-info"
               : item.action === "resubmitted"
               ? "badge-warning"
               : "badge-primary";
   
           return (
             <div key={index} className="history-item">
   
               {/* STATUS BADGE */}
               <div className="history-status">
                 <span className={`history-badge ${badgeClass}`}>
                   {statusLabel}
                 </span>
               </div>
   
               {/* COMMENT */}
               {item.comment && (
                 <div className="history-comment">
                   {item.comment}
                 </div>
               )}
   
               {/* META INFO */}
               <div className="history-meta">
                 <small>
                   {new Date(item.created_at).toLocaleString()}
                   {" • "}
                   <strong>
                     {item.action_by_name?.trim() || "Unknown User"}
                   </strong>
                 </small>
               </div>
   
             </div>
           );
         })}
       </div>
     </div>
   )}
   
   
   <div className="loan-section">
  <h4>Approved Amount</h4>

  <div className="loan-info-grid">
    <div className="loan-info-item">
      <span className="loan-info-label">Edit Approved Amount</span>
      <input
        type="number"
        value={approvedAmount}
        onChange={(e) => setApprovedAmount(e.target.value)}
        className="form-input"
      />
    </div>
  </div>
</div>
   
 {/* ================= ACTIONS ================= */}
<div className="modal-actions">

  <button
    className="btn-primary"
    onClick={() => setConfirmAction("update")}
    disabled={saving}
  >
    Update Amount
  </button>

  <button
    className="btn-danger"
    onClick={() => setConfirmAction("close")}
    disabled={saving}
  >
    Close Loan
  </button>

</div>
       </div>
     )}
   </BaseModal>




<BaseModal
  isOpen={isReportModalOpen}
  title="Download Approved Loans Report"
  onClose={() => setIsReportModalOpen(false)}
  size="md"
>
  <div className="loan-details-grid">
    <div className="loan-section">
      <h4>Report Filters</h4>

      <div className="loan-info-grid">
        <div className="loan-info-item">
          <span className="loan-info-label">Start Date</span>
          <input
            type="date"
            value={reportStartDate}
            onChange={(e) => setReportStartDate(e.target.value)}
            className="form-input"
          />
        </div>

        <div className="loan-info-item">
          <span className="loan-info-label">End Date</span>
          <input
            type="date"
            value={reportEndDate}
            onChange={(e) => setReportEndDate(e.target.value)}
            className="form-input"
          />
        </div>

        <div className="loan-info-item report-type-field">
          <span className="loan-info-label">Report Type</span>
          <select
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
            className="form-input"
          >
            <option value="summary_rm">Summary per RM</option>
            <option value="detailed">Detailed Approved Loans</option>
          </select>
        </div>
      </div>
    </div>

    <div className="modal-actions">
      <button
        className="btn-outline"
        onClick={() => setIsReportModalOpen(false)}
      >
        Cancel
      </button>

      <button
        className="btn-primary"
        onClick={handleDownloadReport}
      >
        Download CSV
      </button>
    </div>
  </div>
</BaseModal>


   {/* ================= CONFIRMATION MODAL ================= */}
<BaseModal
  isOpen={!!confirmAction}
  title="Confirm Action"
  onClose={() => setConfirmAction(null)}
  size="sm"
>
  <div style={{ padding: "10px 0" }}>
    <p>
      {confirmAction === "update" &&
        "Are you sure you want to update the approved amount?"}

      {confirmAction === "close" &&
        "Are you sure you want to close this loan? This action cannot be undone."}
    </p>

    <div className="modal-actions">
      <button
        className="btn-outline"
        onClick={() => setConfirmAction(null)}
      >
        Cancel
      </button>

      <button
        className="btn-danger"
        disabled={saving}
        onClick={async () => {
          if (confirmAction === "update") {
            await handleUpdate();
          }

          if (confirmAction === "close") {
            await handleCloseLoan();
          }

          setConfirmAction(null);
        }}
      >
        {saving ? "Processing..." : "Confirm"}
      </button>
    </div>
  </div>
</BaseModal>
   
    </div>
  );
}